import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import Spinner from "@/components/ui/Spinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Horizontal grouped bar chart — bars grow along the X axis. For every category
 * (labelKey, e.g. a brand name) it renders two bars, one per period
 * (valueAKey / valueBKey).
 *
 * Replaces the donut chart on comparison views where two periods need to be
 * compared per brand rather than a single-period distribution.
 */
const ComparisonBarChart = ({
  data = [],
  labelKey = "brand",
  valueAKey = "period_a",
  valueBKey = "period_b",
  seriesALabel = "Period A",
  seriesBLabel = "Period B",
  title,
  isLoaded = false,
  error = null,
  maxItems = 12,
  rowHeight = 34,
  minHeight = 320,
  className = "",
}) => {
  const [colorA, colorB] = useMemo(() => {
    const s = getComputedStyle(document.documentElement);
    const get = (v) => s.getPropertyValue(v).trim();
    return [
      get("--period-a-color") || "#1f2937",
      get("--period-b-color") || "#16a34a",
    ];
  }, []);

  // Sort by the later period (valueB) descending, then keep the top maxItems.
  const rows = useMemo(() => {
    const sorted = [...(data ?? [])].sort(
      (a, b) => (b[valueBKey] ?? 0) - (a[valueBKey] ?? 0),
    );
    return maxItems ? sorted.slice(0, maxItems) : sorted;
  }, [data, valueBKey, maxItems]);

  const chartData = {
    labels: rows.map((r) => r[labelKey]),
    datasets: [
      {
        label: seriesALabel,
        data: rows.map((r) => Number(r[valueAKey]) || 0),
        backgroundColor: hexToRgba(colorA, 0.75),
        borderColor: colorA,
        borderWidth: 1,
        borderRadius: 3,
      },
      {
        label: seriesBLabel,
        data: rows.map((r) => Number(r[valueBKey]) || 0),
        backgroundColor: hexToRgba(colorB, 0.75),
        borderColor: colorB,
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", axis: "y", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { font: { size: 11 }, boxWidth: 12, padding: 8 },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.formattedValue}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: "#f0f0f0" },
        ticks: { font: { size: 11 }, precision: 0 },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 }, autoSkip: false },
      },
    },
  };

  const chartHeight = Math.max(minHeight, rows.length * rowHeight + 60);

  return (
    <div
      className={`bg-white card-radius border h-100 d-flex flex-column${className ? ` ${className}` : ""}`}
      style={{ borderColor: "var(--border)" }}
    >
      {title && (
        <div className="px-3 py-2 border-bottom bg-light">
          <div className="text-sm fw-medium text-dark">{title}</div>
        </div>
      )}

      <div className="p-3">
        {error && (
          <div className="alert alert-danger mb-0" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            An error occurred while loading the data. Please try again later.
          </div>
        )}

        {!error && !isLoaded && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: `${minHeight}px` }}
          >
            <Spinner />
          </div>
        )}

        {!error && isLoaded && rows.length === 0 && (
          <div
            className="d-flex justify-content-center align-items-center text-muted"
            style={{ minHeight: `${minHeight}px` }}
          >
            <div>
              <i className="bi bi-bar-chart me-2"></i>
              No data available
            </div>
          </div>
        )}

        {!error && isLoaded && rows.length > 0 && (
          <div style={{ height: `${chartHeight}px` }}>
            <Bar data={chartData} options={options} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonBarChart;
